// library imports
import { useEffect, useState, useContext } from "react";
import { Flex, Row, Col, Empty, Spin } from "antd";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// component imports
import DataTable from "../common/DataTable";
import CustomBarChart from "../common/CustomBarChart";

export default function SavingReportOverallSummary({
  year,
  dates,
  loading,
  setLoading,
  tableData,
  setTableData,
  chartData,
  setChartData,
  includePv,
  reportModeSite,
}) {
  // fetch wrapper
  const [fetchWrapper] = useFetch();

  // load index context
  const { siteSelected } = useContext(IndexContext);

  // title year
  const [yearTitle, setYearTitle] = useState("");

  // summary table columns
  const columns = [
    {
      title: "Saving Type",
      dataIndex: "SavingType",
      key: "SavingType",
      width: "50%",
    },
    {
      title: "Saving",
      dataIndex: "Saving",
      key: "Saving",
      width: "50%",
    },
  ];

  /** API to get saving report overall summary (table data and chart data) */
  function getSavingReportOverallSummary() {
    setLoading(true);
    setYearTitle("");

    fetchWrapper({
      endpoint_url: "saving_report/getSavingReportOverallSummary",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: dates[0].startOf("month").format("YYYY-MM-DD"),
        end_date: dates[1].endOf("month").format("YYYY-MM-DD"),
        include_pv: includePv,
      },

      onSuccess: (response) => {
        setTableData(response.data.table_data);
        setChartData(response.data.chart_data);
        setYearTitle(response.data.year);
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  useEffect(() => {
    if (
      dates &&
      dates[0] &&
      dates[1] &&
      siteSelected &&
      includePv !== null &&
      reportModeSite === siteSelected
    ) {
      getSavingReportOverallSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, siteSelected, includePv, reportModeSite]);

  useEffect(() => {
    setYearTitle("");
  }, [siteSelected]);

  return (
    <div className="h-100 w-100">
      <Flex justify="center">
        <h2>Summary for the Year {yearTitle}</h2>
      </Flex>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <DataTable
            table_data={tableData}
            table_column={columns}
            loading={loading}
          />
        </Col>
        <Col span={12}>
          {chartData.length === 0 ? (
            <Flex
              align="center"
              justify="center"
              style={{ width: "100%", position: "relative" }}
            >
              {/* Spin overlay */}
              {loading && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    inset: 0, // shorthand for top, right, bottom, left = 0
                  }}
                >
                  <Spin />
                </div>
              )}

              {/* Empty component with dimmed opacity when loading */}
              <div style={{ width: "100%", opacity: loading ? 0.5 : 1 }}>
                <Empty
                  style={{
                    height: 300,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              </div>
            </Flex>
          ) : (
            <Flex
              align="center"
              justify="center"
              style={{ width: "100%", position: "relative" }}
            >
              {/* Spin overlay */}
              {loading && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    inset: 0, // shorthand for top, right, bottom, left = 0
                  }}
                >
                  <Spin />
                </div>
              )}

              <CustomBarChart
                style={{ opacity: loading ? 0.5 : 1 }}
                data={chartData}
                colorBar={"#2389FF"}
                xAxis={"date"}
                yAxis={"saving"}
                xAxisTitle={""}
                yAxisTitle={""}
                height={300}
                frontUnit={"RM"}
              />
            </Flex>
          )}
        </Col>
      </Row>
    </div>
  );
}
